import api from 'api'
import {
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_ROUTES_METADATA_BY_DATE,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
} from 'api/cacheTagTypes'

import type { ChangeDateRouteSlotBody, MoveRouteSlotBody, RouteSlotCountResponse } from 'types/api/route/routeSlotRequests'

interface RouteIdArg {
  routeId: string
}

interface SkipInvalidateArg {
  skipInvalidate?: boolean
}

interface MoveRouteSlotsArgs extends RouteIdArg, SkipInvalidateArg {
  data: MoveRouteSlotBody
}

interface ChangeRouteSlotDateArgs extends RouteIdArg, SkipInvalidateArg {
  data: ChangeDateRouteSlotBody
}

interface ChangeUnassignedSlotDateArgs extends SkipInvalidateArg {
  data: ChangeDateRouteSlotBody
}

// change-date ignores {route-id} and leaves the stops unassigned; swap for the route-less v3 endpoint once it exists.
const NO_ROUTE_ID = '00000000-0000-0000-0000-000000000000'

// Mirrors STOP_MUTATION_TAGS in moveStops.js — the v1 endpoint this supersedes for slot-keyed moves.
export const ROUTE_SLOT_MUTATION_TAGS = [
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
  CACHE_TAG_ROUTES_METADATA_BY_DATE,
] as const

const routeSlotInvalidatesTags = (_result: unknown, _error: unknown, arg: SkipInvalidateArg | undefined): readonly string[] =>
  arg?.skipInvalidate ? [] : [...ROUTE_SLOT_MUTATION_TAGS]

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    moveRouteSlots: builder.mutation<RouteSlotCountResponse, MoveRouteSlotsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/stops/move`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: routeSlotInvalidatesTags,
    }),
    changeRouteSlotDate: builder.mutation<RouteSlotCountResponse, ChangeRouteSlotDateArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/stops/change-date`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: routeSlotInvalidatesTags,
    }),
    changeUnassignedSlotDate: builder.mutation<RouteSlotCountResponse, ChangeUnassignedSlotDateArgs>({
      query: ({ data }) => ({
        url: `/api/v3/route/${NO_ROUTE_ID}/stops/change-date`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: routeSlotInvalidatesTags,
    }),
  }),
})

export const { useMoveRouteSlotsMutation, useChangeRouteSlotDateMutation, useChangeUnassignedSlotDateMutation } = extendedApi
